import React from 'react';
import {
  useRouterContext,
  useServices,
  useSiteContext,
  AppMarketPage,
  QueryDecorator,
  useTranslation,
} from '@wix/app-market-core';
import type { RoutePath, RoutePayloadMap } from '@wix/app-market-core-types';
import { queryApps as queryAppsData } from './collection-page-data';
import type { QueryApps } from '../../components/apps-section';
import { AppsSection } from '../../components/apps-section';
import type { QueryAppsFilter } from '@wix/app-market-services';
import { queryKeyBuilder } from '../../components/apps-section/app-section-helper';
import { AppsSectionSkeleton } from '../../components/apps-section/apps-section-skeletons';
import { Box, EmptyState } from '@wix/design-system';
import { showSaleToggle } from '../../utils/sale-toggle';

export function CollectionAppsSection({ slug }: { slug: string }) {
  const { languageCode } = useSiteContext();
  const { t } = useTranslation();
  const services = useServices();
  const { route } = useRouterContext();

  const isSale = Boolean(
    (route.payload as RoutePayloadMap[RoutePath.COLLECTION]).isSale,
  );
  const pageName = AppMarketPage.COLLECTION;
  const shouldShowSaleToggle = showSaleToggle(route);

  const queryApps: QueryApps = async ({
    offset,
    filter,
  }: {
    offset?: number;
    filter?: QueryAppsFilter;
  }) => {
    const { appGroup, paging, collectionName } = await queryAppsData({
      services,
      slug,
      languageCode,
      offset,
      filter,
    });

    return {
      appGroup,
      paging,
      collectionName,
    };
  };

  const EmptyStateComponent = ({
    isFilterOn,
    collectionName,
  }: {
    isFilterOn?: boolean;
    collectionName?: string;
  }) => (
    <Box marginTop="12px" direction="vertical">
      <EmptyState
        title={
          isFilterOn
            ? t('collections.empty.state.with.filter.title')
            : t('collectionsEmptyState.title')
        }
        subtitle={
          <Box maxWidth="480px">
            {isFilterOn
              ? t('collections.empty.state.with.filter.subtitle', {
                  collectionName,
                  interpolation: { escapeValue: false },
                })
              : t('collectionsEmptyState.subtitle')}
          </Box>
        }
        theme="page"
      />
    </Box>
  );

  return (
    <QueryDecorator
      queryFn={() =>
        queryAppsData({
          services,
          slug,
          languageCode,
          offset: 0,
          filter: {
            isSale,
          },
        })
      }
      queryKey={queryKeyBuilder({
        queryName: 'initial-apps-fetch',
        language: languageCode,
        isSale,
        appsLength: 0,
        queryId: slug,
      })}
    >
      {({ data, isLoading }) =>
        isLoading ? (
          <AppsSectionSkeleton showSaleToggle={shouldShowSaleToggle} />
        ) : (
          <AppsSection
            origin={pageName}
            queryApps={queryApps}
            queryId={slug}
            initData={data}
            showSaleToggle={shouldShowSaleToggle}
            EmptyState={EmptyStateComponent}
          />
        )
      }
    </QueryDecorator>
  );
}
